import React from 'react';
import HelpArticle from '../../components/help/HelpArticle';

const ToolDamage: React.FC = () => {
  const content = (
    <>
      <h2>Hvad sker der hvis et værktøj går i stykker?</h2>
      <p>
        En guide til hvordan man håndterer situationen når værktøj bliver beskadiget under leje.
      </p>

      <h3>Første ting først: Vær ærlig</h3>
      <p>
        Uanset om du er lejer eller ejer, er ærlighed afgørende:
      </p>
      <ul>
        <li>Rapportér skader med det samme</li>
        <li>Undgå at skjule eller bagatellisere</li>
        <li>Kommunikér åbent</li>
        <li>Arbejd sammen om en løsning</li>
      </ul>

      <h3>Typer af skader</h3>

      <h4>Normal slitage</h4>
      <p>
        Forventet ved normal brug:
      </p>
      <ul>
        <li>Let ridser på overflade</li>
        <li>Mindre sløvhed på blade</li>
        <li>Lidt støv eller snavs</li>
        <li>Normalt kalkproto for slid</li>
      </ul>
      <p>
        <strong>Ansvar:</strong> Ejeren. Dette er en del af normal udlejning.
      </p>

      <h4>Mindre skader ved uheld</h4>
      <p>
        Utilsigtede hændelser:
      </p>
      <ul>
        <li>Du tabte det og fik et lille knæk</li>
        <li>Kabler blev beskadiget ved et uheld</li>
        <li>Mindre funktionsfejl efter normal brug</li>
      </ul>
      <p>
        <strong>Ansvar:</strong> Primært lejer, men forsikring kan dække.
      </p>

      <h4>Større skader</h4>
      <p>
        Alvorlige problemer:
      </p>
      <ul>
        <li>Motor brændte sammen</li>
        <li>Væsentlige dele gik i stykker</li>
        <li>Kan ikke repareres</li>
        <li>Værktøjet er totalt ødelagt</li>
      </ul>
      <p>
        <strong>Ansvar:</strong> Lejer, kræver forsikring eller fuld erstatning.
      </p>

      <h4>Misbrug/forsømmelighed</h4>
      <p>
        Skader fra forkert brug:
      </p>
      <ul>
        <li>Brugt til forkert formål</li>
        <li>Ignoreret sikkerhedsinstruktioner</li>
        <li>Efterladt i regn/vådt miljø</li>
        <li>Overladt ulåst og stjålet</li>
      </ul>
      <p>
        <strong>Ansvar:</strong> Fuldt lejerens ansvar, forsikring dækker måske ikke.
      </p>

      <h3>Hvis du er lejeren</h3>

      <h4>Straks efter skaden sker</h4>
      <ol>
        <li><strong>Stop brugen:</strong> Brug ikke værktøjet mere</li>
        <li><strong>Sikkerhed først:</strong> Sørg for ingen er i fare</li>
        <li><strong>Tag billeder:</strong> Dokumentér skaden fra flere vinkler</li>
        <li><strong>Kontakt ejeren:</strong> Ring eller send besked med det samme</li>
      </ol>

      <h4>Hvad skal du sige?</h4>
      <p>
        Vær ærlig og direkte:
      </p>
      <blockquote>
        "Hej [Navn], jeg er meget ked af det, men [værktøj] er desværre gået i stykker. [Kort forklaring af hvad der skete]. Jeg har taget billeder som jeg sender nu. Hvad vil du have jeg skal gøre?"
      </blockquote>

      <h4>Diskutér løsningen</h4>
      <p>
        Tal med ejeren om:
      </p>
      <ul>
        <li>Kan det repareres?</li>
        <li>Hvad vil det koste?</li>
        <li>Er dit depositum nok til at dække?</li>
        <li>Skal du kontakte din forsikring?</li>
        <li>Kan du selv stå for reparationen?</li>
      </ul>

      <h4>Hvis depositum dækker</h4>
      <p>
        Den nemmeste løsning:
      </p>
      <ul>
        <li>Ejeren får reparationstilbud</li>
        <li>Holder hele eller den af depositum</li>
        <li>I er færdige når reparation er betalt</li>
      </ul>

      <h4>Hvis depositum ikke dækker</h4>
      <p>
        Ved større skader:
      </p>
      <ol>
        <li>Kontakt din indboforsikring (ansvarsdækning)</li>
        <li>Forklar situationen</li>
        <li>Medgiv dokumentation (billeder, DoMore booking, ejerens oplysninger)</li>
        <li>De vurderer om det er dækket</li>
        <li>Hvis godkendt, betaler de erstatning til ejeren</li>
      </ol>

      <h3>Hvis du er ejeren</h3>

      <h4>Når lejeren informerer dig</h4>
      <ol>
        <li><strong>Bevar roen:</strong> Ulykker sker</li>
        <li><strong>Få detaljer:</strong> Hvad præcis skete?</li>
        <li><strong>Bed om billeder:</strong> Hvis de ikke allerede har sendt dem</li>
        <li><strong>Inspicér selv:</strong> Når værktøjet returneres</li>
      </ol>

      <h4>Vurder skaden</h4>
      <ul>
        <li>Kan det repareres?</li>
        <li>Hvad vil det koste?</li>
        <li>Er det normal slid eller ægte skade?</li>
        <li>Var det misbrug eller uheld?</li>
      </ul>

      <h4>Få reparationstilbud</h4>
      <ul>
        <li>Tag værktøjet til en fagmand</li>
        <li>Få skriftligt tilbud</li>
        <li>Del tilbuddet med lejeren</li>
        <li>Vær rimelig - søg ikke profit</li>
      </ul>

      <h4>Diskutér kompensation</h4>
      <p>
        Mulige løsninger:
      </p>
      <ul>
        <li><strong>Depositum dækker:</strong> Hold helt eller delvist</li>
        <li><strong>Lejer betaler reparation:</strong> Send regning når det er fixet</li>
        <li><strong>Lejerens forsikring:</strong> Arbejd med deres forsikringsselskab</li>
        <li><strong>Din forsikring:</strong> Anmeld på din egen police</li>
      </ul>

      <h4>Hvis værktøjet er total loss</h4>
      <ul>
        <li>Få vurdering af værktøjets værdi (brugt, ikke ny)</li>
        <li>Dokumentér med kvitteringer hvis du har</li>
        <li>Foreslå fuld erstatning</li>
        <li>Arbejd med lejerens forsikring</li>
        <li>Alternativt brug din egen forsikring</li>
      </ul>

      <h3>Forsikringsprocessen</h3>

      <h4>Gennem lejerens ansvarsforsikring</h4>
      <ol>
        <li>Lejer kontakter deres forsikring</li>
        <li>Forsikringen kontakter dig for detaljer</li>
        <li>Du medgiver dokumentation (billeder, tilbud, kvitteringer)</li>
        <li>Forsikringen vurderer kravet</li>
        <li>Hvis godkendt, får du erstatning</li>
        <li>Typisk uden selvrisiko for ejeren</li>
      </ol>

      <h4>Gennem din egen forsikring</h4>
      <ol>
        <li>Du anmelder skaden</li>
        <li>Medgiver dokumentation</li>
        <li>De vurderer om det er dækket</li>
        <li>Du betaler selvrisiko (2.500-5.000 DKK)</li>
        <li>Forsikringen dækker resten</li>
      </ol>

      <h3>Hvad hvis lejer nægter ansvar?</h3>

      <h4>Hvis de siger "Det var allerede ødelagt"</h4>
      <p>
        Derfor er dokumentation vigtig:
      </p>
      <ul>
        <li>Vis dine billeder fra før udlejning</li>
        <li>Revisér jeres DoMore chat (dokumenterede de skaden ved afhentning?)</li>
        <li>Kontakt DoMore support med dokumentation</li>
      </ul>

      <h4>Hvis de bare ignorerer dig</h4>
      <ol>
        <li>Send påmindelser via DoMore chat (dokumentation)</li>
        <li>Giv rimelig frist (48-72 timer)</li>
        <li>Kontakt DoMore support</li>
        <li>Efterlad ærlig anmeldelse</li>
        <li>Ved store beløb: overvej juridisk hjælp</li>
      </ol>

      <h3>Forebyggelse</h3>

      <h4>For ejere</h4>
      <ul>
        <li><strong>Dokumentér tilstand:</strong> Tag detaljerede billeder før hver udlejning</li>
        <li><strong>Giv instruktioner:</strong> Vis hvordan det bruges korrekt</li>
        <li><strong>Vedligehold værktøj:</strong> Sørg for det er i god stand</li>
        <li><strong>Kræv depositum:</strong> Specielt for dyrt værktøj</li>
        <li><strong>Verificér forsikring:</strong> Sørg for dit værktøj er dækket</li>
        <li><strong>Tjek ratings:</strong> Udlej til pålidelige brugere</li>
      </ul>

      <h4>For lejere</h4>
      <ul>
        <li><strong>Inspicér ved afhentning:</strong> Dokumentér eksisterende skader</li>
        <li><strong>Få instruktioner:</strong> Lær at bruge det korrekt</li>
        <li><strong>Læs manualen:</strong> Forstå begrænsninger</li>
        <li><strong>Brug ansvarligt:</strong> Kun til designeret formål</li>
        <li><strong>Opbevar sikkert:</strong> Beskyt mod tyveri og vejr</li>
        <li><strong>Verificér forsikring:</strong> Tjek at du har ansvarsdækning</li>
      </ul>

      <h3>Hvornår er du IKKE ansvarlig?</h3>

      <h4>Som lejer, du er ikke ansvarlig hvis</h4>
      <ul>
        <li>Skaden eksisterede ved afhentning (og du dokumenterede det)</li>
        <li>Det gik i stykker ved normal brug pga. dårlig vedligeholdelse</li>
        <li>Ejerens instruktioner var forkerte</li>
        <li>Fabrikationsfejl eller tilbagekaldelse</li>
      </ul>

      <h4>Som ejer, du skal acceptere</h4>
      <ul>
        <li>Normal slitage</li>
        <li>Kosmetiske ridser fra normal brug</li>
        <li>Slid på konsumable dele (blade, batterier)</li>
      </ul>

      <h3>Rimeligt vs. urimeligt</h3>

      <h4>Rimelige forventninger fra ejer</h4>
      <ul>
        <li>✅ Værktøj returneres rent</li>
        <li>✅ Ingen nye skader ud over normal slid</li>
        <li>✅ Alle dele er med</li>
        <li>✅ Det fungerer som ved afhentning</li>
      </ul>

      <h4>Urimelige forventninger fra ejer</h4>
      <ul>
        <li>❌ At det er "som nyt" efter brug</li>
        <li>❌ Professionel rengøring</li>
        <li>❌ Opgradering eller forbedringer</li>
        <li>❌ At holde depositum uden faktisk skade</li>
      </ul>

      <h4>Rimelige forventninger fra lejer</h4>
      <ul>
        <li>✅ Værktøj virker som beskrevet</li>
        <li>✅ Sikkerhedsfunktioner fungerer</li>
        <li>✅ Korrekte instruktioner gives</li>
        <li>✅ Fair behandling ved uheld</li>
      </ul>

      <h4>Urimelige forventninger fra lejer</h4>
      <ul>
        <li>❌ At ejeren accepterer grov uagtsomhed</li>
        <li>❌ Gratis udskiftning ved deres fejl</li>
        <li>❌ Depositum tilbage selvom værktøj er ødelagt</li>
      </ul>

      <h3>Tips til god kommunikation</h3>
      <p>
        Ved skader, kommunikér effektivt:
      </p>
      <ul>
        <li>Vær ærlig og transparent</li>
        <li>Forklar roligt hvad der skete</li>
        <li>Tag ansvar hvis det er din fejl</li>
        <li>Vær rimelig i krav og forventninger</li>
        <li>Arbejd mod en løsning, ikke en konflikt</li>
        <li>Dokumentér al kommunikation</li>
      </ul>

      <h3>Når alt andet fejler</h3>
      <p>
        Hvis I ikke kan blive enige:
      </p>
      <ul>
        <li>Kontakt DoMore support for mægling</li>
        <li>Overvej professionel vurdering af skaden</li>
        <li>Ved store beløb: juridisk rådgivning</li>
        <li>Brug anmeldelser til at advare andre</li>
      </ul>

      <h3>Lær af oplevelsen</h3>
      <p>
        Uanset udfaldet:
      </p>
      <ul>
        <li>Reflektér over hvad der gik galt</li>
        <li>Tænk på hvordan det kunne forebygges</li>
        <li>Dele din erfaring i anmeldelser</li>
        <li>Hjælp fællessket med at lære</li>
      </ul>

      <h3>Bundle line</h3>
      <p>
        De fleste skade-situationer løses nemt når:
      </p>
      <ul>
        <li>Begge parter er ærlige</li>
        <li>Der er god dokumentation</li>
        <li>Forsikring står klar til at hjælpe</li>
        <li>Alle er rimelige</li>
      </ul>
      <p>
        Med respekt og åben kommunikation kan selv dårlige situationer ende positivt.
      </p>
    </>
  );

  return (
    <HelpArticle
      title="Hvad sker der hvis et værktøj går i stykker?"
      category="Populært"
      content={content}
      relatedArticles={[
        { title: 'Forsikringsdækning', link: '/help/insurance-coverage' },
        { title: 'Afhentning og returnering', link: '/help/pickup-return' },
        { title: 'Tvister og support', link: '/help/dispute-support' },
      ]}
    />
  );
};

export default ToolDamage;
